import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { quoteApi, scheduleApi } from '../../api/endpoints.ts';
import { dispatchApi } from '../../api/dispatch.ts';
import { DispatchProposalsPanel } from '../schedule/dispatch-sections.tsx';
import type { DispatchProposal } from '@shared/dispatch.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { invalidateQuoteViews } from '../../state/quote-cache.ts';
import { usePlanFeature } from '../plan-gate.tsx';
import { Button, classes, ConfirmDialog, Select } from '../ui.tsx';
import { AlreadyPaidOnQuote } from '../payments/quote-payments.tsx';
import { rangeBandFor } from './plain-price.tsx';
import { ScopeOption } from './first-clean-card.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { DEFAULT_VISIT_START, suggestedFirstVisit } from '@shared/quotes/booking.ts';
import { todayInTimezone } from '../schedule/format.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import {
  canStaffAccept,
  isPartlyScheduledQuote,
  isScheduledQuote,
  LOCKED_QUOTE_STATUSES,
  type QuoteStatus,
  type SeriesFrequency,
} from '@shared/constants/index.ts';
import type { Quote, QuoteFrequencyTier, QuoteSummary } from '@shared/types/index.ts';
import { formatLocalTime, formatNumericDate } from '../../i18n/datetime.ts';

/**
 * The offered tiers, when the copy in hand carries them. Both of `asking`'s
 * sources return the FULL quote (the list button re-reads it, the editor's
 * `prepare` is a save), so in practice a tiered quote always brings its tiers
 * here; a bare summary simply gets no picker rather than a wrong one.
 */
function offeredTiersOf(candidate: QuoteSummary): QuoteFrequencyTier[] {
  if (!candidate.frequencyTiersEnabled) return [];
  return 'frequencyTiers' in candidate ? (candidate as { frequencyTiers: QuoteFrequencyTier[] }).frequencyTiers : [];
}

/**
 * "Put this on the calendar", offered from wherever a quote is listed.
 *
 * The point is that booking work should cost one decision, not a tour of the
 * app. Saying yes puts the first visit on the calendar. The dialog asks for
 * that day (tomorrow, unless someone picks another) and the time. A quote the
 * customer has already said yes to in person — still a draft, still unsent,
 * whatever it is — takes the same step, and the acceptance the office would
 * otherwise have had to record by hand first rides along with it.
 *
 * The two are deliberately not the same button. Ready-to-book is the brand
 * green everything else affirmative uses; the one that also changes the
 * quote's status is on the warning ramp and says so in its label — the color
 * is the second telling, never the only one (spec §27), because a button that
 * quietly accepts a quote on someone's behalf is not a button anybody should
 * press by accident. Pressing it opens the question in full: which quote,
 * whose, for how much, and what accepting it means.
 */
export function ScheduleQuoteButton({
  quote,
  prepare,
  className,
  showExistingScheduleLink = true,
}: {
  quote: QuoteSummary;
  /**
   * Run before accepting, for a caller holding unsaved edits (the editor).
   * Returns the quote as the server now has it, whose `version` the accept is
   * sent with — accepting at a stale version is a conflict, not a save.
   */
  prepare?: () => Promise<QuoteSummary | null>;
  className?: string;
  showExistingScheduleLink?: boolean;
}) {
  const { t } = useTranslation();
  const { can, company, currentLocation } = useSession();
  const hasScheduling = usePlanFeature('scheduling');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /**
   * The quote the open question is about — as the server has it, never the
   * draft on screen. Null while nothing is being asked.
   */
  const [asking, setAsking] = useState<QuoteSummary | null>(null);
  const [startsOn, setStartsOn] = useState('');
  const [startTime, setStartTime] = useState(DEFAULT_VISIT_START);
  const [dayTaken, setDayTaken] = useState<string | null>(null);
  const [notice, setNotice] = useState<'saveFailed' | 'checkFailed' | 'unavailable' | 'alreadyScheduled' | null>(null);
  /** The tier the customer said yes to, when the quote offers a choice. */
  const [chosenFirstClean, setChosenFirstClean] = useState('');
  const [chosenFrequency, setChosenFrequency] = useState<SeriesFrequency | ''>('');
  /** The dispatcher's pick from the top-three proposals; null books unassigned, as before. */
  const [proposal, setProposal] = useState<DispatchProposal | null>(null);

  const status = quote.status as QuoteStatus;
  const alreadyAccepted = LOCKED_QUOTE_STATUSES.includes(status);
  // Fully on the calendar — every schedule this quote promises exists.
  // Outranks everything below: whatever else is true of it, the work it sold
  // is booked, and the useful thing to offer is the way to that booking
  // rather than another one.
  //
  // One schedule covers the quote, including a contract sold at several
  // visits a week — those days are weekdays of that schedule, added by
  // editing it.
  const alreadyScheduled = isScheduledQuote(quote) && !('bookingPending' in quote && quote.bookingPending);
  // Some but not all of them. The one state that wants both controls: a link
  // to what is on the calendar, and the button for what is not.
  const partlyScheduled = isPartlyScheduledQuote(quote);
  // An expired quote refuses every transition but cancellation, and the date
  // is fixed on the quote itself — so this is left out rather than offered as
  // a button that cannot work.
  const acceptable = canStaffAccept(status) && !quote.isExpired && can('quotes:write');

  /**
   * What can be done with a quote as the server has it *now*.
   *
   * Every decision after the first click goes through here rather than reusing
   * what the button was rendered from, because the click itself can change the
   * answer and so can anyone else in the meantime. Saving an accepted quote
   * reopens it as a draft revision (`reopenedFromAccepted` in the Worker), so
   * "already accepted" stops being true precisely when someone edits the terms
   * — booking on the strength of the pre-save status would hang a visit off a
   * revision the customer never agreed to. A quote that has since been
   * declined, canceled or left to expire has to stop the flow rather than be
   * asked about again.
   */
  function whatNow(candidate: QuoteSummary): 'book' | 'ask' | 'scheduled' | 'unavailable' {
    const now = candidate.status as QuoteStatus;
    // Checked before anything else, and separately from `unavailable`,
    // because "somebody already booked this" is a different sentence from
    // "this can no longer be booked" — and the first one has a link at the
    // end of it. The window it closes is real: two people on the same
    // accepted quote both see a Schedule button, and without this the second
    // click books the work twice.
    if (isScheduledQuote(candidate) && !('bookingPending' in candidate && candidate.bookingPending)) return 'scheduled';
    // Partly booked falls through: there is a schedule left to place, so the
    // ordinary booking path is exactly right for it.
    if (LOCKED_QUOTE_STATUSES.includes(now)) return 'book';
    if (canStaffAccept(now) && !candidate.isExpired && can('quotes:write')) return 'ask';
    return 'unavailable';
  }

  function bookingToday(): string {
    return todayInTimezone(currentLocation?.timezone ?? company?.timezone);
  }

  function openBooking(ready: QuoteSummary) {
    setStartsOn(ready.requestedVisit?.startsOn ?? suggestedFirstVisit(bookingToday()));
    setStartTime(ready.requestedVisit?.startTimeLocal ?? DEFAULT_VISIT_START);
    setDayTaken(null);
    setProposal(null);
    setChosenFirstClean('acceptedFirstClean' in ready ? ((ready as Quote).acceptedFirstClean?.id ?? '') : '');
    setChosenFrequency(whatNow(ready) === 'ask' ? '' : (ready.acceptedFrequency ?? ''));
    setAsking(ready);
  }

  function proceedWith(ready: QuoteSummary) {
    const next = whatNow(ready);
    if (next === 'book' || next === 'ask') {
      openBooking(ready);
    } else if (next === 'scheduled') {
      // Nothing was booked and nothing was accepted — the read simply found a
      // quote that is further along than the copy this button was drawn from.
      // The re-read below repaints it as the link to that schedule.
      setAsking(null);
      setNotice('alreadyScheduled');
      invalidateQuoteViews(queryClient);
    } else {
      setAsking(null);
      setNotice('unavailable');
    }
  }

  /**
   * Gets to the truth before doing anything with it, then asks or goes.
   *
   * Both callers hold a copy of the quote that the server may disagree with,
   * so neither is trusted to decide. The editor's is a draft: its save has to
   * happen *before* the question, because live pricing sits in a preview while
   * the quote carries the last saved totals — asking first would put the
   * pre-edit total in front of the reader and then accept the post-edit one. A
   * refused save (offline, a conflict, a rejected edit) stops the whole thing
   * rather than accepting content the server never received; the editor's own
   * banner says which refusal it was.
   *
   * A list row is a copy too, up to half a minute old and older still on a
   * page left open. What it says is Accepted may since have been edited by
   * someone else, which reopens it as a draft revision — and the series
   * endpoint checks only that the quote exists, so nothing downstream would
   * catch a visit hung off terms nobody agreed to. One read settles it, and
   * pre-fills the booking form from what is true now rather than what the row
   * remembers.
   */
  const begin = useMutation({
    mutationFn: async () => (prepare ? await prepare() : await quoteApi.get(quote.id)),
    onSuccess: (ready) => {
      if (!ready) {
        setNotice('saveFailed');
        return;
      }
      setNotice(null);
      proceedWith(ready);
    },
    // A refused save and an unreachable server are different problems and read
    // differently; which one this is depends only on what was being attempted.
    onError: () => setNotice(prepare ? 'saveFailed' : 'checkFailed'),
  });

  function refreshQuotes() {
    invalidateQuoteViews(queryClient);
  }

  async function bookThis(ready: QuoteSummary) {
    const needsAccept = !LOCKED_QUOTE_STATUSES.includes(ready.status as QuoteStatus);
    // A first clean built from the quote is its one price: named outright, so
    // a retry after a lost response can prove it is the same acceptance.
    const only = quoteFirstCleanOf(ready);
    const firstCleanOptionId = only?.id ?? chosenFirstClean;
    const booked = await quoteApi.acceptAndBook(ready.id, {
      version: ready.version,
      ...(firstCleanOptionId ? { firstCleanOptionId } : {}),
      allowUnsent: true,
      startsOn,
      ...(startTime ? { startTimeLocal: startTime } : {}),
      ...(needsAccept && chosenFrequency ? { frequency: chosenFrequency } : {}),
    });
    // The chosen cleaner is assigned only now, after the dispatcher confirmed
    // the booking, and a weekly schedule then reserves their next twelve
    // weeks. Either step failing leaves a booked, unassigned schedule the
    // series page can fix, so neither blocks the navigation.
    const picked = proposal;
    if (picked && picked.startsOn === startsOn && picked.startTimeLocal === startTime) {
      try {
        await scheduleApi.updateSeries(booked.seriesId, { assignedUserIds: [picked.userId] });
        await dispatchApi.reserve(booked.seriesId, picked.userId).catch(() => undefined);
      } catch {
        // Assignment refused (for example a clash appeared): left for the series page.
      }
    }
    refreshQuotes();
    setAsking(null);
    navigate(`/schedule/series/${booked.seriesId}`);
  }

  const accept = useMutation({
    mutationFn: (ready: QuoteSummary) => bookThis(ready),
    /**
     * The quote moved under the question while it was on screen.
     *
     * A spent version, or a status that no longer permits accepting, answers
     * 409 and the quote is read again. A taken day is a different 409: the
     * quote stays accepted and the dialog stays up so another day can be
     * picked. Already booked is a third: nothing else is created.
     */
    onError: async (error) => {
      if (!(error instanceof ApiRequestError) || error.status !== 409) return;
      if (error.code === 'VISIT_DAY_TAKEN') {
        setDayTaken(error.message);
        refreshQuotes();
        const fresh = await quoteApi.get(quote.id).catch(() => null);
        if (fresh && LOCKED_QUOTE_STATUSES.includes(fresh.status as QuoteStatus)) {
          setAsking(fresh);
          setChosenFrequency(fresh.acceptedFrequency ?? '');
          setChosenFirstClean(fresh.acceptedFirstClean?.id ?? '');
        }
        return;
      }
      if (error.code === 'ALREADY_SCHEDULED') {
        setAsking(null);
        setNotice('alreadyScheduled');
        refreshQuotes();
        return;
      }
      refreshQuotes();
      const fresh = await quoteApi.get(quote.id).catch(() => null);
      if (!fresh) return;
      // A refreshed acceptance may belong to another option or version.
      // Show the current agreement and wait for a new explicit confirmation.
      accept.reset();
      proceedWith(fresh);
    },
  });

  // No calendar on this plan, so no handoff to one: the button would land on
  // a page that explains it cannot book anything.
  if (!hasScheduling) return null;

  // Booked. The one thing worth offering here is the way to the schedule this
  // quote became — a link, not a button, because following it changes
  // nothing. Rendered before the acceptance gates below, since a booked quote
  // that somebody has since edited back into a draft revision would otherwise
  // fall through them and show nothing at all, hiding the schedule it still
  // has on the calendar.
  //
  // And before the write gate, on `schedule:read`, because this link does not
  // write: it goes to the series page, which that scope already opens (and
  // which the same role reaches from the customer's own schedule section).
  // Gating it on `schedule:write` would hide a readable page from its readers.
  if (alreadyScheduled) {
    if (!can('schedule:read')) return null;
    return (
      <Link to={`/schedule/series/${quote.scheduledSeriesId}`} className={classes('btn btn-secondary', className)}>
        {t('quotes.schedule.viewSchedule')}
      </Link>
    );
  }

  // Everything below books something, so from here the write scope applies.
  if (!can('schedule:write')) return null;
  if (!alreadyAccepted && !acceptable) return null;

  return (
    <>
      {/* The schedules already placed against a multi-visit contract, beside
          the button for the ones still to place. Without it the office has no
          way back to what it booked a moment ago except the calendar. */}
      {showExistingScheduleLink && partlyScheduled && quote.scheduledSeriesId && can('schedule:read') ? (
        <Link to={`/schedule/series/${quote.scheduledSeriesId}`} className="btn btn-ghost">
          {t('quotes.schedule.viewSchedule')}
        </Link>
      ) : null}

      <Button
        variant={alreadyAccepted ? 'primary' : 'warning'}
        className={className}
        busy={begin.isPending}
        onClick={() => begin.mutate()}
      >
        {alreadyAccepted
          ? t(quote.requestedVisit ? 'quotes.schedule.reviewRequest' : 'quotes.schedule.action')
          : t('quotes.schedule.acceptAndAction')}
      </Button>

      {notice ? <p className="field-error">{t(`quotes.schedule.${notice}Error`)}</p> : null}

      {asking
        ? (() => {
            const scopes =
              ('firstClean' in asking ? (asking as import('@shared/types/index.ts').Quote).firstClean?.options : []) ??
              [];
            const onlyScope = quoteFirstCleanOf(asking);
            const chosenScope = onlyScope ?? scopes.find((o) => o.id === chosenFirstClean);
            const acceptedScope = 'acceptedFirstClean' in asking ? (asking as Quote).acceptedFirstClean : null;
            const tiers = offeredTiersOf(asking);
            const chosenTier = tiers.find((tier) => tier.frequency === chosenFrequency);
            const needsAccept = !LOCKED_QUOTE_STATUSES.includes(asking.status as QuoteStatus);
            // What accepting a price range commits to: the band for the price
            // being accepted, never its middle.
            const band = rangeBandFor(asking, chosenTier?.totalCents ?? asking.totalCents);
            return (
              <ConfirmDialog
                title={t(needsAccept ? 'quotes.schedule.confirmTitle' : 'quotes.schedule.confirmTitleBook', {
                  number: asking.quoteNumber,
                })}
                confirmLabel={t(needsAccept ? 'quotes.schedule.confirmAction' : 'quotes.schedule.confirmActionBook')}
                busy={accept.isPending}
                // The choice is the price, so no yes without one. The day is
                // the visit, so no booking without one either.
                confirmDisabled={
                  (needsAccept && tiers.length > 0 && !chosenTier) ||
                  (needsAccept && scopes.length > 0 && !chosenScope) ||
                  !startsOn
                }
                error={dayTaken ?? (accept.isError ? (accept.error as Error).message : null)}
                onCancel={() => {
                  setAsking(null);
                  setDayTaken(null);
                  accept.reset();
                }}
                onConfirm={() => {
                  setDayTaken(null);
                  accept.mutate(asking);
                }}
              >
                <p>
                  {asking.pricePromise === 'range' && needsAccept && (tiers.length === 0 || chosenTier)
                    ? // A price range has no total to accept: the band's middle is not a price.
                      band
                      ? t('quotes.schedule.confirmBodyRangeBand', {
                          customer: asking.customerNameSnapshot,
                          low: formatCents(band.lowCents, asking.currencyCode, getDateLocale()),
                          high: formatCents(band.highCents, asking.currencyCode, getDateLocale()),
                        })
                      : t('quotes.schedule.confirmBodyRange', { customer: asking.customerNameSnapshot })
                    : needsAccept && tiers.length > 0 && chosenTier
                      ? t('quotes.schedule.confirmBody', {
                          customer: asking.customerNameSnapshot,
                          // The chosen tier's price — the figure the acceptance
                          // is about to write onto the quote.
                          total: formatCents(chosenTier.totalCents, asking.currencyCode, getDateLocale()),
                        })
                      : needsAccept && tiers.length > 0
                        ? t('quotes.schedule.confirmBodyChooseFrequency', { customer: asking.customerNameSnapshot })
                        : needsAccept
                          ? t('quotes.schedule.confirmBody', {
                              customer: asking.customerNameSnapshot,
                              // The saved total, which is the one about to be accepted.
                              total: formatCents(
                                chosenScope?.firstVisit.pricing.total_cents ?? asking.totalCents,
                                asking.currencyCode,
                                getDateLocale(),
                              ),
                            })
                          : t('quotes.schedule.confirmBodyBook', { customer: asking.customerNameSnapshot })}
                </p>
                {needsAccept && onlyScope && <ScopeOption option={onlyScope} currency={asking.currencyCode} />}
                {needsAccept && scopes.length > 0 && !onlyScope && (
                  <>
                    <Select
                      label={t('firstClean.choice')}
                      value={chosenFirstClean}
                      onChange={(e) => {
                        setChosenFirstClean(e.target.value);
                        setProposal(null);
                      }}
                      options={[
                        { value: '', label: t('firstClean.choose') },
                        ...scopes.map((o) => ({ value: o.id, label: o.name })),
                      ]}
                    />
                    {chosenScope && <ScopeOption option={chosenScope} currency={asking.currencyCode} />}
                  </>
                )}
                {!needsAccept && acceptedScope && <ScopeOption option={acceptedScope} currency={asking.currencyCode} />}
                {asking.pricePromise === 'range' ? (
                  <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('quotes.schedule.rangeNote')}</p>
                ) : null}
                {asking.requestedVisit ? (
                  <p className="mt-2" role="status">
                    {t('quotes.schedule.requestedVisit', {
                      date: formatNumericDate(asking.requestedVisit.startsOn),
                      time: formatLocalTime(asking.requestedVisit.startTimeLocal),
                      timezone: asking.requestedVisit.timezone,
                    })}
                  </p>
                ) : null}
                {can('schedule:read') && (
                  <DispatchProposalsPanel
                    quoteId={asking.id}
                    frequency={needsAccept && chosenFrequency ? chosenFrequency : undefined}
                    firstCleanOptionId={needsAccept ? chosenScope?.id : undefined}
                    chosen={proposal}
                    onChoose={(picked) => {
                      setProposal(picked);
                      if (picked) {
                        setStartsOn(picked.startsOn);
                        setStartTime(picked.startTimeLocal);
                      }
                    }}
                  />
                )}
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="field-label">{t('quotes.schedule.firstVisitLabel')}</span>
                    <input
                      type="date"
                      className="field-control min-h-11"
                      min={bookingToday()}
                      value={startsOn}
                      onChange={(event) => setStartsOn(event.target.value)}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="field-label">{t('quotes.schedule.timeLabel')}</span>
                    <input
                      type="time"
                      className="field-control min-h-11"
                      value={startTime}
                      onChange={(event) => setStartTime(event.target.value)}
                    />
                  </label>
                </div>
                <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('quotes.schedule.comingHint')}</p>
                <AlreadyPaidOnQuote quoteId={asking.id} className="mt-2" />
                {needsAccept && tiers.length > 0 ? (
                  <div className="mt-3">
                    <Select
                      label={t('quotes.schedule.frequencyLabel')}
                      value={chosenFrequency}
                      onChange={(event) => {
                        setChosenFrequency(event.target.value as SeriesFrequency | '');
                        // Proposals are re-checked for the new cadence; the old pick no longer stands.
                        setProposal(null);
                      }}
                      options={[
                        { value: '', label: t('quotes.schedule.frequencyPlaceholder') },
                        ...tiers.map((tier) => {
                          // A price range offers each frequency as the band
                          // choosing it gives, never that band's middle.
                          const tierBand = rangeBandFor(asking, tier.totalCents);
                          return {
                            value: tier.frequency,
                            label: t('quotes.schedule.frequencyOption', {
                              frequency: t(`schedule.frequency.${tier.frequency}`),
                              amount: tierBand
                                ? t('quotes.list.range', {
                                    low: formatCents(tierBand.lowCents, asking.currencyCode, getDateLocale()),
                                    high: formatCents(tierBand.highCents, asking.currencyCode, getDateLocale()),
                                  })
                                : formatCents(tier.totalCents, asking.currencyCode, getDateLocale()),
                            }),
                          };
                        }),
                      ]}
                    />
                  </div>
                ) : null}
                {needsAccept ? (
                  <p className="mt-2 text-[color:var(--color-muted)]">{t('quotes.schedule.confirmNote')}</p>
                ) : null}
              </ConfirmDialog>
            );
          })()
        : null}
    </>
  );
}

/** The quote's own first clean, when it has one: a single price, not a choice to make. */
function quoteFirstCleanOf(quote: QuoteSummary) {
  const bundle = 'firstClean' in quote ? (quote as Quote).firstClean : null;
  return bundle?.areaFirstClean && bundle.options.length === 1 ? bundle.options[0] : undefined;
}
